import { i18n } from "@/plugins/i18n";
import { computed } from "vue";
import offersHelper from "@/utils/helpers/offers";
import type { Offer } from "@/types/Offers";
import { createMoveManagerContext, type MoveManagerContext, type MoveManagerProps } from "./moveManagerBase";

export function useMoveManagerPricePart(props?: MoveManagerProps, ctx?: MoveManagerContext) {
	const { n } = i18n.global;
	const context = ctx ?? createMoveManagerContext(props);
	const { currentOffer, demand, projectSelected, servicesPrice, nbDaysBookingLimit } = context;

	const calcArrhesAmount = computed(() => (
		currentOffer.value
			? (arrhesPercent.value / 100)
				* (offersHelper.getPrice(currentOffer.value, !!exclTax.value) + servicesPrice.value)
			: null
	));
	const arrhesAmount = computed(() => (currentOffer.value ? `${n(calcArrhesAmount.value ?? 0, 'currency')}` : null));
	const arrhesAlreadyPaid = computed(() => demand?.value?.arrhes_already_paid);
	const floaAlreadyPaid = computed(() => demand?.value?.floa_already_paid);

	const calcPrice = computed(() => (currentOffer.value ? offersHelper.getPrice(currentOffer.value, !!exclTax.value) : null));
	const price = computed(() => (
		currentOffer.value
			? `${n((calcPrice.value ?? 0) * (arrhesPercent.value / 100), 'currency')}`
			: null
	));

	const ceiling = computed(() => currentOffer.value && offersHelper.getRepaymentCeiling(currentOffer.value));
	const priceDiff = computed(() => currentOffer.value && ceiling.value && Math.round((offersHelper.getPrice(currentOffer.value, !!exclTax.value) + servicesPrice.value - ceiling.value) * 100) / 100);
	const warningVisible = computed(() => currentOffer.value && projectSelected.value?.params?.ceiling && (ceiling.value || 0) > 0 && (priceDiff.value || 0) > 0);
	const priceType = computed(() => exclTax.value ? 'price_ht' : 'price');
	const hidePriceInfo = computed(() => projectSelected.value?.params?.hide_price_info);
	const arrhesPercent = computed(() => projectSelected.value?.params?.arrhes_percent ?? 0);
	const exclTax = computed(() => demand?.value?.show_price_ht);
	const priceTypeKey = computed(() => demand?.value?.priceTypeKey);

	const getTextParams = computed(() => ({
		price: n((calcPrice.value ?? 0), 'currency'),
		nbDaysBookingLimit: nbDaysBookingLimit.value,
		arrhesAmount: arrhesAmount.value,
		solde: n(((calcPrice.value ?? 0) - (calcArrhesAmount.value ?? 0)) || 0, 'currency'),
		exclTax: exclTax.value ? 'HT' : 'TTC',
		moverName: currentOffer.value?.mover?.name,
	}));
	
	const getPrice = (offerToPrice: Offer) => {
		return offersHelper.getPrice(offerToPrice, !!exclTax.value);
	};

	return {
		calcArrhesAmount,
		arrhesAmount,
		arrhesAlreadyPaid,
		floaAlreadyPaid,
		calcPrice,
		exclTax,
		arrhesPercent,
		price,
		ceiling,
		priceDiff,
		warningVisible,
		priceType,
		hidePriceInfo,
		priceTypeKey,
		getTextParams,
		getPrice,
	};
}

